import type { HTMLAttributes, ReactNode } from "react";

export interface ContextBadgeProps extends HTMLAttributes<HTMLSpanElement> {
  children: ReactNode;
}

/**
 * ContextBadge — the small chip in the panel header naming the active
 * page context ("Thrive Search", "Thrive Contact", "LinkedIn Recruiter").
 *
 * v17 `.context-badge` (line 437): inline-flex with 3px gap, 2px 8px
 * padding, 10px (PILL) radius, 10px font, 600 weight, accent-muted bg,
 * accent text, 0.2px letter-spacing. NOT uppercase in v17 — `Thrive
 * Search` is in title case (the upper-case treatment was my mistake).
 *
 * Tokenized: the 10px / 600-weight badge text → `text-micro`
 * (10/1.3/600 — the role already carries 600 weight). `rounded-full`
 * is unchanged — it's a Tailwind default that now resolves to the
 * `--radius-full` (9999px) token, so the fully-rounded badge shape is
 * preserved 1:1.
 */
export function ContextBadge({ className, children, ...rest }: ContextBadgeProps) {
  const base =
    "inline-flex items-center gap-0.5 rounded-full bg-accent-muted px-2 py-0.5 text-micro tracking-[0.02em] text-accent";
  const classes = [base, className].filter(Boolean).join(" ");
  return (
    <span {...rest} className={classes}>
      {children}
    </span>
  );
}
